import {
  $createLineBreakNode,
  $createParagraphNode,
  $getRoot,
  $isLineBreakNode,
  $isParagraphNode,
  type EditorState,
  type LexicalEditor,
  type LexicalNode,
} from 'lexical'
import { type EffectiveTextStyle, type RichTextRun } from '@backend/lib/db/typeRoots/layoutNode'
import { $createRichTextNode, $isRichTextNode, RichTextNode } from './RichTextNode'

/** Build the initial Lexical editor state from a runs array. Splits runs on
 *  embedded `\n` so each line break becomes a LineBreakNode, matching the
 *  hard-break convention used by `expandCanvasDesign.ts` (a `\n` run inherits
 *  the previous run's style, so we don't need to materialize style for it). */
export const populateEditorFromRuns = (runs: RichTextRun[]): void => {
  const root = $getRoot()
  root.clear()
  const paragraph = $createParagraphNode()

  for (const run of runs) {
    const segments = run.text.split('\n')
    for (let i = 0; i < segments.length; i++) {
      if (i > 0) paragraph.append($createLineBreakNode())
      const segment = segments[i]!
      if (segment.length > 0) {
        paragraph.append($createRichTextNode(segment, runStyle(run)))
      }
    }
  }

  root.append(paragraph)
}

/** Read the editor state back as a runs array. Walks the single paragraph
 *  child by child, emitting one RichTextRun per RichTextNode and a `\n` run
 *  per LineBreakNode. The `\n` run carries the preceding RichTextNode's
 *  style so downstream consumers (rewrap, drawCanvas) see a consistent
 *  shape. */
export const readRunsFromEditorState = (
  editorState: EditorState,
  fallbackStyle: EffectiveTextStyle,
): RichTextRun[] =>
  editorState.read(() => {
    const root = $getRoot()
    const runs: RichTextRun[] = []
    let lastStyle: EffectiveTextStyle = fallbackStyle

    for (const block of root.getChildren()) {
      if (!$isParagraphNode(block)) continue
      for (const child of block.getChildren()) {
        if ($isRichTextNode(child)) {
          const style = child.getRunStyle()
          const text = child.getTextContent()
          // Preserve empty styled runs: after select-all-delete inside a
          // styled span, the RichTextNode lingers as an empty placeholder
          // so the caret keeps that style for the next typed character.
          // Skipping it here would lose the style on round-trip and
          // typed chars would inherit the previous run's style instead.
          runs.push({ text, ...style })
          lastStyle = style
        } else if ($isLineBreakNode(child)) {
          runs.push({ text: '\n', ...lastStyle })
        }
      }
    }

    if (runs.length === 0) {
      runs.push({ text: '', ...fallbackStyle })
    }
    return runs
  })

/** Convenience for callers that only have a LexicalEditor handle. */
export const readRunsFromEditor = (
  editor: LexicalEditor,
  fallbackStyle: EffectiveTextStyle,
): RichTextRun[] => readRunsFromEditorState(editor.getEditorState(), fallbackStyle)

const runStyle = (run: RichTextRun): EffectiveTextStyle => ({
  fontFamily: run.fontFamily,
  fontSize: run.fontSize,
  fontWeight: run.fontWeight,
  fontStyle: run.fontStyle,
  fontColor: run.fontColor,
})

/** Deep equality on a runs array — text + the five glyph-style fields plus
 *  the fontColor RGBA + the optional palette `index`. Used by the editor's
 *  blur path and the edit-cycle eval to detect whether a round-trip through
 *  Lexical mutated anything. Missing the `index` here would silently drop
 *  swatch-picker updates that change palette membership without changing
 *  the displayed RGBA. */
export const runsEqual = (a: RichTextRun[], b: RichTextRun[]): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const ar = a[i]!
    const br = b[i]!
    if (
      ar.text !== br.text ||
      ar.fontFamily !== br.fontFamily ||
      ar.fontSize !== br.fontSize ||
      ar.fontWeight !== br.fontWeight ||
      ar.fontStyle !== br.fontStyle ||
      ar.fontColor.r !== br.fontColor.r ||
      ar.fontColor.g !== br.fontColor.g ||
      ar.fontColor.b !== br.fontColor.b ||
      ar.fontColor.a !== br.fontColor.a ||
      ar.fontColor.index !== br.fontColor.index
    ) {
      return false
    }
  }
  return true
}

/** Find the RichTextNode that holds (or last held) the caret. Used by future
 *  selection-mode toolbar work to surface the run-at-caret style. */
export const findRichTextNodeAtCaret = (selectionAnchorNode: LexicalNode): RichTextNode | null => {
  let cursor: LexicalNode | null = selectionAnchorNode
  while (cursor) {
    if ($isRichTextNode(cursor)) return cursor
    cursor = cursor.getParent()
  }
  return null
}
